import { useTranslation } from 'react-i18next';
import { Building2, Check, ChevronDown } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../lib/api';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from './ui/DropdownMenu';

interface Entity {
  id: string;
  name: string;
  country_code: string;
  base_currency: string;
}

/**
 * Top-bar entity switcher (spec §6.3). Lists every legal entity under
 * the current workspace; the selection lives in localStorage so the
 * choice is preserved across reloads. Hidden entirely if the workspace
 * has zero or one entity (avoids visual noise on small accounts).
 */
const KEY = 'plyez.activeEntity';

export function EntitySwitcher({ workspaceId }: { workspaceId: string }) {
  const { t } = useTranslation();
  const { data: entities = [] } = useQuery<Entity[]>({
    queryKey: ['entities', workspaceId],
    enabled: !!workspaceId,
    queryFn: async () => {
      try {
        return (await api.get<Entity[]>(`/workspaces/${workspaceId}/entities`)).data;
      } catch {
        return [];
      }
    },
    staleTime: 5 * 60_000,
  });

  const [activeId, setActiveId] = useState<string | null>(() => {
    if (typeof window === 'undefined') return null;
    return window.localStorage.getItem(`${KEY}:${workspaceId}`);
  });

  if (entities.length <= 1) return null;

  const active = entities.find((e) => e.id === activeId) ?? entities[0];

  function pick(id: string) {
    setActiveId(id);
    if (typeof window !== 'undefined') window.localStorage.setItem(`${KEY}:${workspaceId}`, id);
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="hidden md:flex items-center gap-1.5 h-8 px-2.5 rounded-lg border border-ink-200 dark:border-ink-700 bg-white dark:bg-ink-900 text-xs hover:border-ink-400">
          <Building2 size={12} className="text-ink-400" />
          <span className="max-w-[140px] truncate">{active.name}</span>
          <ChevronDown size={12} className="text-ink-400" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuLabel>{t('topbar.entityLabel')}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {entities.map((e) => (
          <DropdownMenuItem key={e.id} onSelect={() => pick(e.id)}>
            <Building2 size={12} className="text-ink-400" />
            <span className="flex-1 truncate">{e.name}</span>
            <span className="text-[10px] font-mono text-ink-400">{e.country_code} · {e.base_currency}</span>
            {e.id === active.id && <Check size={12} className="ml-1 text-emerald-600" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
